import {
  ref,
  onValue,
  query,
  orderByChild,
  push,
  set,
  get,
} from "firebase/database"

import {
  LOG_ADD_SUCCESS,
  LOG_ADD_ERROR,
  LOG_ADD_START,
  USER_LESSONS_GET_SUCCESS,
  UPDATE_USER_OPTION,
  UPDATE_FIREBASE_PROFILE,
  UPDATE_LAST_ACTIVE_UPDATED_AT,
} from "./types"
import { sdk9db } from "../firebase"

const NUM_RECENT_TO_SHOW = 10
const LAST_ACTIVE_UPDATE_FREQUENCY_SECONDS = 1 * 60 * 60 // 1 hour

/*
 * Required keys:
 * userId, type (auto or manual), action (like "madePayment")
 *
 * Optional keys:
 * lessonId, changes, amountDollars, note
 */
export const addLog = (log) => (dispatch, getState) => {
  const {
    firebase: {
      auth: { uid },
    },
  } = getState()

  if (!log.userId || !uid) return Promise.resolve()

  const proms = []
  // If it's a cancellation, we add it in two places.
  if (log.action === "deleteLesson") {
    proms.push(
      push(ref(sdk9db, "cancelLog"), {
        ...log,
        timestamp: new Date().valueOf(),
        changedById: uid,
      }),
    )
  }

  proms.push(
    push(ref(sdk9db, `userLog/${log.userId}`), {
      ...log,
      timestamp: new Date().valueOf(),
      changedById: uid,
    }),
  )

  return Promise.all(proms)
    .then(dispatch({ type: LOG_ADD_SUCCESS }))
    .catch((error) => dispatch({ type: LOG_ADD_ERROR, payload: error }))
}

export const addLessonChangeLog =
  (
    userId,
    prevLesson,
    curLesson,
    action,
    lessonId,
    note = "",
    initiatedBy = "",
  ) =>
  (dispatch) => {
    dispatch({ type: LOG_ADD_START })

    // Build the changes object.
    const changes = {}
    if (curLesson && !prevLesson) {
      // It's new so everything changed.
      changes.instructor = {
        new: curLesson.instructor,
      }
      // Now loop through the lesson details object.
      Object.keys(curLesson.details).forEach((key) => {
        changes[key] = {
          new: curLesson.details[key],
        }
      })
    } else if (!curLesson && prevLesson) {
      // The lesson was deleted, so everything changed to nothing.
      changes.instructor = {
        old: prevLesson.instructor,
      }
      // Now loop through the lesson details object.
      Object.keys(prevLesson.details).forEach((key) => {
        changes[key] = {
          old: prevLesson.details[key],
        }
      })
    } else if (curLesson && prevLesson !== curLesson) {
      // There was a lesson before, so figure out what changed.
      // Check the instructor.
      if (prevLesson.instructor.id !== curLesson.instructor.id) {
        changes.instructor = {
          old: prevLesson.instructor,
          new: curLesson.instructor,
        }
      }
      // Now loop through the lesson details object.
      // Assumes the keys don't change.
      Object.keys(prevLesson.details).forEach((key) => {
        if (prevLesson.details[key] !== curLesson.details[key]) {
          changes[key] = {
            old: prevLesson.details[key],
            new: curLesson.details[key],
          }
        }
      })
    }

    const log = {
      userId,
      type: "auto",
      action,
      changes,
      lessonId,
      note,
      initiatedBy,
    }
    dispatch(addLog(log))
  }

export const getUserLessons = (userId) => (dispatch) => {
  if (!userId) return null
  const lessonSlotsQuery = query(
    ref(sdk9db, `studentLessonSlotsV2/${userId}`),
    orderByChild("details/startTime"),
  )
  // TODO Where do we detach this listener?
  return onValue(lessonSlotsQuery, (lessonsSnap) => {
    const lessons = []
    lessonsSnap.forEach((lessonSnap) => {
      const lesson = lessonSnap.val()
      if (lesson) {
        lesson.id = lessonSnap.key
        lessons.push(lesson)
      }
    })
    dispatch({
      type: USER_LESSONS_GET_SUCCESS,
      payload: lessons,
    })
  })
}

export const updateUserOption = (option) => ({
  type: UPDATE_USER_OPTION,
  payload: option,
})

// If there are more than 10, we need to remove older ones.
// So just get what we have, truncate as needed, and then
// add our new one, and rewrite the current user's entire list.
export const adminVisitedUser = (adminUserId, studentId, name) => {
  const studentHistoryQuery = query(
    ref(sdk9db, `adminStudentHistory/${adminUserId}`),
    orderByChild("timestamp"),
  )
  return get(studentHistoryQuery).then((recentsSnap) => {
    const recents = []
    recentsSnap.forEach((recentSnap) => {
      const recent = recentSnap.val()
      recent.id = recentSnap.key
      recents.push(recent)
    })
    const recentsSlice = recents.reverse().slice(0, NUM_RECENT_TO_SHOW)
    recentsSlice.push({
      id: studentId,
      timestamp: new Date().valueOf(),
      name,
    })
    const updates = {}
    recentsSlice.forEach((item) => {
      updates[item.id] = item
    })
    return set(ref(sdk9db, `adminStudentHistory/${adminUserId}`), updates)
  })
}

// This code follows the same logic as the one used in
// adminVisitedUser
export const adminVisitedInstructor = (adminUserId, instructorId, name) => {
  const instructorHistoryQuery = query(
    ref(sdk9db, `adminInstructorHistory/${adminUserId}`),
    orderByChild("timestamp"),
  )
  return get(instructorHistoryQuery).then((recentsSnap) => {
    const recents = []
    recentsSnap.forEach((recentSnap) => {
      const recent = recentSnap.val()
      recent.id = recentSnap.key
      recents.push(recent)
    })
    const recentsSlice = recents.reverse().slice(0, NUM_RECENT_TO_SHOW)
    recentsSlice.push({
      id: instructorId,
      timestamp: new Date().valueOf(),
      name,
    })
    const updates = {}
    recentsSlice.forEach((item) => {
      updates[item.id] = item
    })
    return set(ref(sdk9db, `adminInstructorHistory/${adminUserId}`), updates)
  })
}

export const updateProfile = (profile) => ({
  type: UPDATE_FIREBASE_PROFILE,
  payload: profile,
})

export const maybeUpdateLastActive = (userId) => (dispatch, getState) => {
  const { user } = getState()
  const currentTime = new Date().valueOf()
  if (
    !user.lastActiveUpdatedAt ||
    currentTime - user.lastActiveUpdatedAt >
      LAST_ACTIVE_UPDATE_FREQUENCY_SECONDS * 1000
  ) {
    set(ref(sdk9db, `users/${userId}/lastActive`), new Date().valueOf())
      .then(() => {
        dispatch({
          type: UPDATE_LAST_ACTIVE_UPDATED_AT,
        })
      })
      .catch((error) => {
        // We're okay with permission denied as it can happen with user write race
        // conditions with auth.
        if (error.code === "PERMISSION_DENIED") return
        throw new Error(error)
      })
  }
}
